import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getPropertyDisplayName } from 'ui-deal-management-components-lib/utils/dealPropertyUtils';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { CURRENCY_PRECISION } from '../../constants/Currency';
import Links from '../../constants/Links';
import { GOALS_TAB } from '../../constants/OverviewConstants';
import { ForecastAmountSettingOptions } from '../../constants/PortalConfigConstants';
import { getIsUngatedToForecastingMultipleOutcomes } from '../../selectors/authSelectors';
import { getDealsList, getDealsTotal, getTotalOrWeightedDealAmount } from '../../selectors/dealsSelectors';
import { getDealGroupingTypeFilter, getFilters } from '../../selectors/filtersSelectors';
import { getPortalConfig, getSelectedForecastType } from '../../selectors/forecastSelectors';
import { getCompanyCurrency } from '../../selectors/multiCurrencySelectors';
import { getFiscalOffset } from '../../selectors/portalSettings';
import { getProperties } from '../../selectors/properties';
import { getTeamsMap } from '../../selectors/usersSelectors';
import { computePipelineQueryParam, getFilteredDealsUrl } from '../../utils/urls';
import { trackViewDealsInCrmFromSidebar, trackViewGoalsInCrmFromSidebar } from '../../utils/usageTracker';
import { makeDealFilterGroupQuery } from '../../utils/filters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_DEALS_PER_PAGE = 25;
export const DealSidebarFooter = ({
  selectedTab,
  isAllPipelinesSelected,
  selectedPipelines,
  id,
  isTeam,
  isDealStageView
}) => {
  var _useSelector;
  const currencyCode = useSelector(getCompanyCurrency);
  const dealGroupingType = useSelector(getDealGroupingTypeFilter);
  const deals = useSelector(getDealsList);
  const dealsTotal = useSelector(getDealsTotal);
  const filterState = useSelector(getFilters);
  const fiscalOffset = (_useSelector = useSelector(getFiscalOffset)) !== null && _useSelector !== void 0 ? _useSelector : 0;
  const isUngatedToForecastingMultipleOutcomes = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  const portalConfig = useSelector(getPortalConfig);
  const properties = useSelector(getProperties);
  const selectedForecastType = useSelector(getSelectedForecastType);
  const teamsMap = useSelector(getTeamsMap);
  const totalDealAmount = useSelector(getTotalOrWeightedDealAmount);
  const handleTrackViewDealsInCrmFromSidebar = useCallback(() => {
    trackViewDealsInCrmFromSidebar(isDealStageView);
  }, [isDealStageView]);
  const handleTrackViewGoalsInCrmFromSidebar = useCallback(() => {
    trackViewGoalsInCrmFromSidebar(isDealStageView);
  }, [isDealStageView]);
  const totalAmount = totalDealAmount || {
    currencyCode,
    value: 0
  };
  const showDealsTotalOver25 = deals.size >= MAX_DEALS_PER_PAGE;
  const renderDealLabel = () => {
    if (!isUngatedToForecastingMultipleOutcomes) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT ? 'dealSidebar.weightedDealAmount' : 'dealSidebar.totalDealAmount'
      });
    }
    const selectedForecastTypeAmountProperty = getPropertyDisplayName(properties, selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.amountProperty);
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT ? 'dealSidebar.weightedAmountProperty' : 'dealSidebar.totalAmountProperty',
      options: {
        amountProperty: selectedForecastTypeAmountProperty
      }
    });
  };
  const dealsUrl = getFilteredDealsUrl(makeDealFilterGroupQuery({
    dealGroupingType,
    filterState,
    id,
    isTeam,
    teams: teamsMap,
    fiscalYearOffset: fiscalOffset
  }), computePipelineQueryParam(isAllPipelinesSelected, selectedPipelines));
  if (selectedTab === GOALS_TAB) {
    return /*#__PURE__*/_jsx(PanelFooter, {
      display: "flex",
      flexDirection: "column",
      padding: "0",
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        paddingBlockStart: "400",
        paddingBlockEnd: "400",
        children: /*#__PURE__*/_jsx(Link, {
          external: true,
          href: Links.GOALS,
          onClick: handleTrackViewGoalsInCrmFromSidebar,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "dealSidebar.viewInGoals"
          })
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(PanelFooter, {
    display: "flex",
    flexDirection: "column",
    padding: "0",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignSelf: "stretch",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(Flex, {
        borderBlockColor: "border-core-subtle",
        borderBlockStyle: "solid",
        borderBlockEndWidth: "200",
        borderBlockStartWidth: "0",
        paddingBlockStart: "400",
        paddingBlockEnd: "100",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "center",
          flexGrow: 1,
          children: [/*#__PURE__*/_jsxs(Text, {
            typography: "body-125",
            children: [renderDealLabel(), /*#__PURE__*/_jsx(Spacer, {
              display: "inline-block",
              space: "100"
            })]
          }), /*#__PURE__*/_jsx(FormattedIntlCurrency, {
            currencyCode: totalAmount.currencyCode,
            precision: CURRENCY_PRECISION,
            value: totalAmount.value
          })]
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "center",
        paddingBlockStart: "100",
        paddingBlockEnd: "400",
        children: [showDealsTotalOver25 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "dealSidebar.dealsTotalOver25",
            options: {
              total: dealsTotal
            }
          }), /*#__PURE__*/_jsx(Spacer, {
            display: "inline-block",
            space: "100"
          })]
        }), /*#__PURE__*/_jsx(Link, {
          external: true,
          href: dealsUrl,
          onClick: handleTrackViewDealsInCrmFromSidebar,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: showDealsTotalOver25 ? 'dealSidebar.viewAllDeals' : 'dealSidebar.viewInDeals'
          })
        })]
      })]
    })
  });
};